import { CanvasMark } from '../plane/CanvasMark';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  DragEvent,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  ButtonHTMLAttributes,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useVault } from '../session/useVault';
import { useWhenRefused } from '../session/useWhenRefused';
import { usePlan } from '../plan/usePlan';
import { flat, parseEntity } from '../entity/entity';
import type { VaultEntity } from '../entity/entity';
import { failedBeforeQueued, pendingText, pendingWrite, withdrawWrites } from '../vault/writeQueue';
import type { HeldWrite } from '../vault/writeQueue';
import type { FileChange } from '../vault/sparseWriter';
import { buildCorpus } from '../thoughts/corpus';
import { withList } from '../plan/edits';
import type { Arc } from '../plan/arc';
import { listOf } from '../entity/entity';
import { useCatalog } from '../entity/catalogStore';
import { EMPTY_REGISTRIES, tagTint } from '../plan/registries';
import { CARD_WIDTH, canvasGround, markInk } from '../design/tokens';
import { NookGlyph } from './NookGlyph';
import { TAG_COLOR_IDS, TAG_COLORS, tagTones } from '../design/tagPalette';
import type { TagColorId } from '../design/tagPalette';
import { useTheme } from '../design/useTheme';
import type { Theme } from '../design/tokens';
import {
  linksRebased,
  placementsRebased,
  readLinks,
  readPlane,
  refKey,
  withLinks,
  withCardOpen,
  withPlane,
  withTint,
} from '../plane/planeKey';
import type { PlaneLink, PlanePlacement, PlaneRef, StickyTint } from '../plane/planeKey';
import { SIDES } from '../plane/curveGeometry';
import type { Box, Point, Side } from '../plane/curveGeometry';
import { useLinks } from '../plane/useLinks';
import { arcTitleOf, cardHeight, LINE, NOOK_WIDTH, STICKY_SIZE, stickyHeight, whenHeightSettles } from '../plane/cardSize';
import { useTable } from '../plane/useTable';
import { Table } from '../plane/Table';
import { PageBar } from '../plane/PageBar';
import { Tray, TrayAdd, TrayList, useTrayLifted } from '../plane/Tray';
import type { TrayEntry } from '../plane/Tray';
import { recencyOf } from '../plan/chronology';
import { boundsOf } from '../plane/tableFit';
import { useCardSizes } from '../plane/useCardSizes';
import { BarHang, CardHang, pageSize, useCloseOnGround } from '../plane/Hang';
import { hangLeftOf, hangUnder, hangUnderRight } from '../plane/hangPlace';
import { EditableName } from '../design/EditableName';
import { isDraftNook, isScratch, isUnnamed, keeping, nameOf, nookTitle, parseWorkspace, startWithOf, waitingOnYou, withNookName, withoutDraft } from '../workspaces/workspace';
import { pinAtFront, pinnedIndex, pinnedNooks, withoutPinned } from '../workspaces/pinned';
import { PinMenu } from '../workspaces/PinMenu';
import { useWorkspaces } from '../workspaces/useWorkspaces';
import { asTheyStand } from '../workspaces/loadWorkspaces';
import { readDeskRaw } from '../desk/useDeskContainer';
import { asWrites, unlistedOnUnpin } from '../desk/deskActs';
import { NOT_UNPINNED } from '../workspaces/usePins';
import { withVisibility } from '../plan/edits';
import { ScratchClear } from './ScratchClear';
import { monthPrefix, newHeldNoteFile, newNotePath } from '../writing/textNote';
import type { MintedNote } from '../writing/useNoteText';
import { newUlid } from '../entity/catalog';
import { withDropped } from '../membership/membership';
import type { Item } from '../membership/membership';
import { itemFor } from '../thoughts/corpus';
import {
  arcsCiting,
  capturedLabel,
  cardName,
  cardView,
  collapsedLinesOf,
  defaultNote,
  heldArcs,
  heldNoteId,
  heldNotePath,
  heldRefs,
  isJustMade,
  movedBefore,
  panelRows,
  wholeVaultRows,
  canPlace,
  recordingFace,
  placedCards,
  withDefaultNote,
  withMemberLine,
  withPlacedNote,
  withoutHeld,
} from './nook';
import type { CardView, ItemKind, PanelKind, Row } from './nook';
import { NOTE_MIME, REF_MIME } from './dnd';
import { NookWriting } from './NookWriting';
import { StickyPanel, noteTone, useNotePanelSize } from './StickyPanel';
import { NookTagPicker } from './NookTagPicker';
import { NookItemCard } from './NookItemCard';
import { NookThumb, useMiniLook } from '../workspaces/NookCanvas';
import { NookCard as NookBoardCard } from '../workspaces/WorkspacesRoute';
import type { Workspace } from '../workspaces/workspace';
import type { CardShell } from '../thoughts/ThoughtCard';
import { NewArcFlow } from './NewArcFlow';
import type { RecentRecording } from './NewArcFlow';
import { nameTaken, newArcFile, withArcHeld } from './newArc';
import type { NewArc } from './newArc';
import { ArcWindow, DocumentSheet, NookWindow, RecordingWindow } from './NookWindows';
import { ArcLine, PenGlyph, WaitingMark } from './parts';
import { isArcIn, isNookIn } from '../vault/kinds';
import './nook.css';

const WIDTH: Record<ItemKind, number> = { recording: CARD_WIDTH, arc: CARD_WIDTH, note: 220, nook: NOOK_WIDTH.small, other: 260 };
/** For framing only; a card's real height is its text. */
const APPROX_HEIGHT = 150;
/** A press that travels less than this is a click, not a drag. */
const SLOP = 4;

/** A card's height before it is measured: a note's own size or its lines, any other card's lines or
 *  its kind's estimate. */
function heightOf(placement: PlanePlacement, kind: ItemKind): number {
  if (kind === 'note') return stickyHeight(placement, APPROX_HEIGHT);
  return cardHeight(placement, kind, kind === 'arc' ? arcTitleOf(placement.ref) : undefined);
}

interface Press {
  key: string;
  isNote: boolean;
  grab: { dx: number; dy: number };
  start: { x: number; y: number };
  moved: boolean;
}

/**
 * A nook, sat in: a bounded plane holding only what was placed on it, and
 * everything else the nook holds one move away in a panel from the dock.
 *
 * Placement is written to the nook's own `plane:` key — the same key and
 * writer the arc plane uses — once per drop, never per pointer move. What is
 * on the plane opens in place, another nook included: only its window's way
 * in navigates away.
 */
export function NookPage({ entity }: { entity: VaultEntity }) {
  const navigate = useNavigate();
  /** The new-nook start is for a nook opened by the explicit "A new nook" press; every other way in opens on the canvas. */
  const startedNew = (useLocation().state as { newNook?: boolean } | null)?.newNook === true;
  const { state, write, flush } = useVault();
  const { plan: planState } = usePlan();
  const arcs = planState.status === 'ready' ? planState.plan.arcs : null;
  const registries = planState.status === 'ready' ? planState.plan.registries : EMPTY_REGISTRIES;
  const catalog = useCatalog();
  const { theme } = useTheme();
  const path = entity.path;

  /** The file as it stands, which every write starts from. A write this tab
   *  still owes wins over a re-read of the vault, which is older. */
  const [raw, setRaw] = useState(() => pendingText(path) ?? entity.raw);
  const press = useRef<Press | null>(null);
  const justDragged = useRef(false);
  const [drag, setDrag] = useState<{ key: string; x: number; y: number } | null>(null);
  /** A sticky's corner being pulled: where the pull started, and the size it
   *  started from. */
  const pull = useRef<{ key: string; start: { x: number; y: number }; from: { w: number; h: number } } | null>(null);
  const [sized, setSized] = useState<{ key: string; w: number; h: number } | null>(null);
  const [trayOpen, setTrayOpen] = useState(false);
  const [trayKind, setTrayKind] = useState<PanelKind>('nooks');
  const [trayLifted, toggleLift] = useTrayLifted();
  const [trayEl, setTrayEl] = useState<HTMLElement | null>(null);
  const panel: PanelKind | null = trayOpen ? trayKind : null;
  /** Opens the tray on a kind, or closes it. */
  const setPanel = useCallback((kind: PanelKind | null) => {
    if (kind) setTrayKind(kind);
    setTrayOpen(kind !== null);
  }, []);
  /** Where the nook's own arch hung what opened from it: the left edge of the arch. */
  const [panelAnchor, setPanelAnchor] = useState<number | null>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; key: string } | null>(null);
  const [files, setFiles] = useState<ReadonlyMap<string, string | null>>(new Map());
  const surface = useRef<HTMLDivElement>(null);
  /** Open, with the note it shows; a null path is the default note before
   *  the first word makes it. */
  const [writing, setWriting] = useState<{ path: string | null } | null>(null);
  const lastNote = useRef<string | null>(null);
  const barNote = useNotePanelSize(writing?.path ?? null);
  /** Where the writing hangs: the right edge of the Note word. */
  const [noteRight, setNoteRight] = useState<number | null>(null);
  const writeButton = useRef<HTMLButtonElement>(null);
  const [writeHot, setWriteHot] = useState(false);
  /** Something opened in place, over the plane. */
  const [opened, setOpened] = useState<
    { kind: 'recording'; ref: PlaneRef } | { kind: 'arc'; path: string } | { kind: 'nook'; path: string } | null
  >(null);
  const [sheet, setSheet] = useState<PlaneRef | null>(null);
  /** The panels list the whole vault, not only what the nook holds. */
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');
  const [tagOpen, setTagOpen] = useState(false);
  /** The arcs panel turned into the new-arc flow. */
  const [making, setMaking] = useState(false);
  const [recent, setRecent] = useState<RecentRecording[] | null>(null);
  /** An arc just made: its two writes, held for Undo until the receipt goes. */
  const [receipt, setReceipt] = useState<{ held: HeldWrite[]; before: string; arcPath: string } | null>(null);

  useEffect(() => {
    if (press.current || pull.current || connect.drawing()) return;
    setRaw(pendingText(path) ?? entity.raw);
  }, [path, entity.raw]);

  const nook = useMemo(() => parseEntity(path, raw), [path, raw]);
  const placements = useMemo(() => readPlane(nook), [nook]);
  const arcPaths = useMemo(() => heldArcs(nook.frontMatter), [nook]);
  const members = useMemo(() => heldRefs(raw), [raw]);
  const declaredDefault = defaultNote(nook.frontMatter);
  /** The file's own title line first — it is as fresh as the file — then
   *  what the route found: the catalog's name, or the filename. */
  const [cleared, setCleared] = useState(false);
  useEffect(() => setCleared(false), [path]);
  const titled = nookTitle(raw);
  const name = titled ?? (cleared ? '' : entity.title);
  const unnamed = !titled && cleared ? true : isUnnamed(name);
  const scratch = isScratch(nook.frontMatter);
  const startWith = useMemo(
    () =>
      startWithOf(
        flat(nook.frontMatter['start-with']).trim() || null,
        arcs ? arcPaths.map((p) => arcs.find((a) => a.path === p)).filter((a): a is Arc => !!a) : null,
      ),
    [nook, arcs, arcPaths],
  );
  const [clearing, setClearing] = useState(false);
  /** Grows each time the menu's Rename asks for the name to be written in. */
  const [renaming, setRenaming] = useState(0);
  /** A note open in its panel: a placed one by its path, or one started on empty canvas, at the
   *  point it was started, until its first word makes it. [tint] is a colour picked before then. */
  const [noting, setNoting] = useState<{
    path: string | null;
    at: Point | null;
    tint?: StickyTint | TagColorId;
    /** Opened by the bar's Note word, which closes it again. */
    fromBar?: boolean;
  } | null>(null);

  const nooks = useWorkspaces();
  const arcsByName = useMemo(() => (arcs ? new Map(arcs.map((a) => [a.name, a])) : null), [arcs]);
  /** The nook opened in place: another one from the canvas or the panel, or this one. */
  const ownOpen = opened?.kind === 'nook' && opened.path === path;
  const openedNook = useMemo(() => {
    if (opened?.kind !== 'nook') return null;
    if (opened.path === path) return parseWorkspace(path, raw);
    return nooks.status === 'ready' ? (nooks.workspaces.find((w) => w.path === opened.path) ?? null) : null;
  }, [opened, nooks, path, raw]);
  const corpus = useMemo(() => (state.status === 'ready' ? buildCorpus(state.vault) : []), [state]);
  /** Whether the panel lists the whole vault: always for nooks, which are never held. */
  const wholeVault = panel === 'nooks' || showAll;
  const vaultRows = useMemo(
    () =>
      panel && wholeVault
        ? wholeVaultRows({
            panel,
            nookPath: path,
            arcPaths,
            members,
            arcs,
            corpus,
            nooks: nooks.status === 'ready' ? nooks.workspaces : [],
            catalog,
            query,
            nowMs: Date.now(),
          })
        : [],
    [panel, wholeVault, path, arcPaths, members, arcs, corpus, nooks, catalog, query],
  );

  // Every file the plane or a panel shows, read in one batch — the default
  // note among them, since the nook holds it. Arcs come from the plan, which
  // already holds them.
  const wanted = useMemo(
    () =>
      [...new Set([...placements, ...members, ...vaultRows.filter((r) => !r.nook).map((r) => r.ref)].map((p) => p.ref))]
        .filter((p) => !isArcIn(p, arcs))
        .sort(),
    [placements, members, vaultRows, arcs],
  );
  const wantedKey = wanted.join('\n');

  useEffect(() => {
    if (state.status !== 'ready' || wanted.length === 0) return;
    let cancelled = false;
    void state.vault.readFiles(wanted).then((read) => {
      if (cancelled) return;
      const byPath = new Map(read.map((r) => [r.path, r.text ?? null]));
      setFiles(new Map(wanted.map((p) => [p, pendingText(p) ?? byPath.get(p) ?? null])));
    });
    return () => {
      cancelled = true;
    };
    // `wanted` is tracked by its key: a new array with the same paths is not a
    // reason to read again.
  }, [state, wantedKey]);

  const cards = useMemo(() => placedCards(placements, arcs, files, catalog), [placements, arcs, files, catalog]);
  const links = useMemo(() => readLinks(nook), [nook]);

  /** Cards drawn open: the ones whose plane line says so, which is the canvas's own and synced.
   *  An open just asked for is drawn open until its measured height is written with it. */
  const [opening, setOpening] = useState<string | null>(null);
  const openKeys = useMemo(() => {
    const out = new Set(placements.filter((p) => p.open).map(refKey));
    if (opening) out.add(opening);
    return out;
  }, [placements, opening]);
  /** The nook's own tag's colour: its canvas's dots, and its own notes. */
  const nookTags = listOf(nook.frontMatter['tags']);
  const nookTag = nookTags[0];
  const nookHue = nookTag ? tagTint(registries, nookTag) : null;
  const nookBand = nookHue ? tagTones(theme, nookHue).band : null;
  const canvas = canvasGround(theme, nookHue);

  /** What a card on the plane wears. An item from elsewhere takes its own
   *  first tag's colour: an arc's from its front matter, anything else's from
   *  its catalog row, untinted until that is known. A sticky left at the
   *  default tint takes the nook's own band. */
  const fillOf = (placement: PlanePlacement, kind: ItemKind): string | undefined => {
    if (kind === 'note') {
      if (placement.tagTint) return tagTones(theme, placement.tagTint).band;
      return placement.tint === undefined && nookBand ? nookBand : undefined;
    }
    const arc = arcs?.find((a) => a.path === placement.ref);
    const tag = arc ? listOf(arc.frontMatter['tags'])[0] : catalog.get(placement.ref)?.tags[0];
    const hue = tag ? tagTint(registries, tag) : null;
    return hue ? tagTones(theme, hue).band : undefined;
  };

  const sizes = useCardSizes(surface, cards);

  /** Where each card is, as drawn right now: mid-drag, mid-resize, measured. */
  const boxes = useMemo(() => {
    const out = new Map<string, Box>();
    for (const { key, placement, view } of cards) {
      const at = drag?.key === key ? drag : placement;
      const size = sized?.key === key ? sized : sizes.get(key);
      out.set(key, {
        x: at.x,
        y: at.y,
        w: size?.w ?? placement.w ?? WIDTH[view.kind],
        h: size?.h ?? heightOf(placement, view.kind),
      });
    }
    return out;
  }, [cards, drag, sized, sizes]);

  /** What the table is fitted to: the cards where they are placed, so a card being dragged does
   *  not move the table under it. */
  const placedBounds = useMemo(
    () =>
      boundsOf(
        cards.map(({ key, placement }) => {
          const { w, h } = boxes.get(key)!;
          return { x: placement.x, y: placement.y, w, h };
        }),
      ),
    [cards, boxes],
  );
  /** The table is fitted once every card is drawn as it will stay: its file read and its size measured. */
  const drawn =
    arcs !== null &&
    cards.length === placements.length &&
    placements.every((p) => isArcIn(p.ref, arcs) || files.has(p.ref)) &&
    cards.every((c) => sizes.has(c.key));
  const table = useTable(placedBounds, path, drawn);
  const cam = table.cam;

  /** A default whose file is gone, or that the nook no longer holds, is no
   *  default: the next Write makes one. */
  const declaredPath = declaredDefault ? heldNotePath(declaredDefault, raw, files) : null;
  const defaultPath = declaredPath && files.get(declaredPath) !== null ? declaredPath : null;

  /** Every write here is a change, made again on the file as it stands when
   *  it goes out: a device may have re-saved the nook since this page read it.
   *  Each is a person's act on the nook, which keeps a draft in the same
   *  write — all but filing it away or back, [keeps] false. */
  const commitChange = useCallback(
    (change: FileChange, keeps = true) => {
      const act = keeps ? keeping(change) : change;
      const text = act(raw);
      if (text === raw) return;
      setRaw(text);
      write([{ path, text, change: act }]);
    },
    [raw, path, write],
  );

  const commit = useCallback(
    (next: PlanePlacement[]) =>
      commitChange((t) => withPlane(t, placementsRebased(readPlane(parseEntity(path, t)), placements, next))),
    [path, placements, commitChange],
  );

  const setOpenOut = (key: string, open: boolean) => {
    if (open) {
      setOpening(key);
      return;
    }
    const card = cards.find((c) => c.key === key);
    const lines = card && collapsedLinesOf(card.placement, card.view, files.get(card.placement.ref));
    commitChange((t) => withCardOpen(t, key, false, lines));
  };

  useEffect(() => {
    if (!opening) return;
    const el = [...(surface.current?.querySelectorAll<HTMLElement>('[data-card-key]') ?? [])].find(
      (e) => e.dataset['cardKey'] === opening,
    );
    if (!el) {
      setOpening(null);
      return;
    }
    return whenHeightSettles(el, (height) => {
      commitChange((t) => withCardOpen(t, opening, true, height / LINE));
      setOpening(null);
    });
  }, [opening, commitChange]);

  const commitLinks = useCallback(
    (next: PlaneLink[]) =>
      commitChange((t) => withLinks(t, linksRebased(readLinks(parseEntity(path, t)), links, next))),
    [path, links, commitChange],
  );

  /** A new name is the file's opening title line, the way an arc's is; the
   *  app reads it back as the nook's name. */
  const rename = useCallback(
    (next: string) => {
      setCleared(!next.trim());
      commitChange((t) => withNookName(t, next));
    },
    [commitChange],
  );

  const pinned = pinnedIndex(nook.frontMatter) !== null;
  const nooksSeen = useRef<readonly Workspace[]>([]);
  nooksSeen.current = nooks.status === 'ready' ? nooks.workspaces : [];
  const rawSeen = useRef(raw);
  rawSeen.current = raw;
  /** A discrete press, so it goes out at once; the head's pins follow the queue. An unpin also
   *  lists the cards the desk shows but no list holds, as an unpin from any page does: this nook
   *  among them where its pin was all that held it on the desk. The container is read first, and
   *  both writes are queued as one act or, where it cannot be read, neither is. */
  const togglePin = useCallback(() => {
    if (!pinned) {
      const pins = nooks.status === 'ready' ? pinnedNooks(nooks.workspaces).map((w) => w.pinned) : [];
      commitChange((t) => pinAtFront(t, pins));
      void flush();
      return;
    }
    const before = parseWorkspace(path, raw);
    if (!before || state.status !== 'ready') {
      commitChange(withoutPinned);
      void flush();
      return;
    }
    const vault = state.vault;
    void (async () => {
      let deskRaw: string | null;
      try {
        deskRaw = await readDeskRaw(vault);
      } catch {
        failedBeforeQueued(NOT_UNPINNED);
        return;
      }
      const nookRaw = rawSeen.current;
      const writes = asWrites(
        [{ path, change: keeping(withoutPinned) }, unlistedOnUnpin(before, () => asTheyStand(nooksSeen.current))],
        (p) => (p === path ? nookRaw : deskRaw),
      );
      const own = writes.find((w) => w.path === path);
      if (!own) return;
      setRaw(own.text);
      write(writes);
      await flush();
    })();
  }, [nooks, pinned, path, raw, state, commitChange, write, flush]);

  const archived = flat(nook.frontMatter['visibility']).trim() === 'archived';
  const isDraft = isDraftNook(raw) && !archived;

  /** Files the nook away, everything on it as it was — for a scratch canvas, the next send
   *  starts a new one; for a draft, it is let go — and goes back to the nooks. */
  const putAway = useCallback(() => {
    setClearing(false);
    commitChange((t) => withVisibility(t, 'archived'), false);
    navigate(isDraft ? '/' : '/nooks');
  }, [commitChange, navigate, isDraft]);

  const restore = useCallback(() => {
    commitChange((t) => withVisibility(t, 'visible'), false);
    void flush();
  }, [commitChange, flush]);

  /** A draft made the person's: its mark taken out, sent at once. */
  const keep = useCallback(() => {
    commitChange(withoutDraft, false);
    void flush();
  }, [commitChange, flush]);

  // The recordings the flow offers: the most recent, newest first, from the
  // corpus Thoughts builds — read when the flow opens.
  useEffect(() => {
    if (!making || state.status !== 'ready') return;
    let cancelled = false;
    const latest = buildCorpus(state.vault)
      .filter((item) => item.kind === 'recording')
      .slice(0, 20)
      .map((item) => item.path);
    void state.vault.readFiles(latest).then((read) => {
      if (cancelled) return;
      const text = new Map(read.map((r) => [r.path, r.text]));
      setRecent(
        latest.flatMap((p) => {
          const raw = text.get(p);
          return raw ? [{ path: p, date: capturedLabel(p, raw), face: recordingFace(p, raw, undefined) }] : [];
        }),
      );
    });
    return () => {
      cancelled = true;
    };
  }, [making, state]);

  /** The new arc and the nook's plan holding it last, written together and
   *  held until the receipt goes, so Undo can take both back. */
  const makeArc = useCallback(
    (arc: NewArc) => {
      const file = newArcFile(arc);
      if (!file) return;
      const holding: FileChange = keeping((t) => withArcHeld(t, path, file.path));
      const nookText = holding(raw);
      const held: HeldWrite[] = [
        { ...file, before: pendingWrite(file.path) },
        { path, text: nookText, change: holding, before: pendingWrite(path) },
      ];
      setReceipt({ held, before: raw, arcPath: file.path });
      setRaw(nookText);
      write([{ ...file, act: file.path }, { path, text: nookText, change: holding, act: file.path }]);
      setMaking(false);
    },
    [raw, path, write],
  );

  // A nook a newer app wrote refuses the plan's change, and the arc with it: neither is in the
  // vault, so the page goes back to the nook as it is and the receipt for the arc goes.
  useWhenRefused(path, () => {
    if (press.current || pull.current || connect.drawing()) return;
    setReceipt(null);
    setRaw(pendingText(path) ?? entity.raw);
  });

  const undoArc = useCallback(() => {
    if (!receipt) return;
    if (withdrawWrites(receipt.held)) setRaw(receipt.before);
    setReceipt(null);
  }, [receipt]);

  // The receipt goes with the debounce that is holding the writes.
  useEffect(() => {
    if (!receipt) return;
    const id = setTimeout(() => setReceipt(null), 5000);
    return () => clearTimeout(id);
  }, [receipt]);

  useEffect(() => {
    if (panel !== 'arcs') setMaking(false);
  }, [panel]);

  /** The nook's colour is its tag; none empties the key, which the app
   *  reads as taking the tag away. */
  const chooseTag = useCallback(
    (tag: string | null) => {
      commitChange((t) => withList(t, 'tags', tag ? [tag] : [], { stateEmpty: true }));
      setTagOpen(false);
    },
    [commitChange],
  );

  const makeDefault = useCallback(
    (notePath: string) => {
      // Named by id or not at all: a path would break the moment the note is
      // renamed. A note no device has adopted and that declares no id waits
      // for its first sync before it can be the default.
      const id = heldNoteId(notePath, raw, files);
      if (id) commitChange((t) => withDefaultNote(t, id));
    },
    [raw, files, commitChange],
  );

  /** A note standing on the plane opens off its sticky, in the one panel a sticky opens; any other
   *  hangs off the bar's Note. */
  const openNote = useCallback(
    (notePath: string | null) => {
      lastNote.current = notePath;
      setPanel(null);
      setOpened(null);
      if (notePath && placements.some((p) => p.ref === notePath && p.anchors.length === 0)) {
        setWriting(null);
        setNoting({ path: notePath, at: null, fromBar: true });
        return;
      }
      const word = writeButton.current?.getBoundingClientRect();
      if (word) setNoteRight(word.right);
      setNoting(null);
      setWriting({ path: notePath });
    },
    [placements],
  );

  /**
   * The nook's first default note, made on the first word typed after Write:
   * the file, its member line and the `write:` key go out as one write, so no
   * device sees a note the nook does not hold.
   */
  const mint = useCallback(
    (first: string): MintedNote | null => {
      if (state.status !== 'ready') return null;
      const now = new Date();
      const taken = new Set(state.vault.listPaths(monthPrefix(now)));
      const fresh = newNotePath(now, (c) => taken.has(c) || files.has(c));
      const id = newUlid();
      const note = newHeldNoteFile(first, id);
      const holding: FileChange = keeping((t) => withDefaultNote(withMemberLine(t, fresh, bare(fresh)), id));
      const nookText = holding(raw);
      setRaw(nookText);
      setFiles((m) => new Map(m).set(fresh, note));
      write([
        { path: fresh, text: note },
        { path, text: nookText, change: holding },
      ]);
      openNote(fresh);
      return { path: fresh, raw: note };
    },
    [state, files, raw, path, write, openNote],
  );

  const noteChanged = useCallback((notePath: string, text: string) => {
    setFiles((m) => new Map(m).set(notePath, text));
  }, []);

  /** A note started on the canvas, made on its first word: the file, and the nook holding and
   *  placing it, in one write. */
  const mintPlaced = useCallback(
    (first: string): MintedNote | null => {
      if (!noting?.at || state.status !== 'ready') return null;
      const now = new Date();
      const taken = new Set(state.vault.listPaths(monthPrefix(now)));
      const fresh = newNotePath(now, (c) => taken.has(c) || files.has(c));
      const note = newHeldNoteFile(first, newUlid(now.getTime()));
      const holding: FileChange = keeping((t) => withPlacedNote(t, path, fresh, noting.at!));
      const nookText = holding(raw);
      setRaw(nookText);
      setFiles((m) => new Map(m).set(fresh, note));
      write([
        { path: fresh, text: note },
        { path, text: nookText, change: holding },
      ]);
      setNoting((n) => n && { ...n, path: fresh });
      return { path: fresh, raw: note };
    },
    [noting, state, files, raw, path, write],
  );

  /** Placed from something opened over the plane, it lands in the middle of what is in view. */
  const placeInView = (ref: PlaneRef, kind: ItemKind) => {
    const rect = surface.current?.getBoundingClientRect();
    if (!rect || placements.some((p) => refKey(p) === refKey(ref))) return;
    const at = canvasAt(rect.left + rect.width / 2, rect.top + rect.height / 2);
    commit([...placements, { ...ref, ...table.keepInView({ x: at.x - WIDTH[kind] / 2, y: at.y - 60 }) }]);
  };

  /** One surface at a time: what opens takes the place of whatever was hanging. */
  const hang = (next: NonNullable<typeof opened>) => {
    setNoting(null);
    setSheet(null);
    setPanel(null);
    setWriting(null);
    setOpened(next);
  };
  const openRecording = (ref: PlaneRef) => hang({ kind: 'recording', ref });
  const openArc = (arcPath: string) => hang({ kind: 'arc', path: arcPath });
  const openNook = (nookPath: string) => hang({ kind: 'nook', path: nookPath });

  const overWrite = (clientX: number, clientY: number) => {
    const r = writeButton.current?.getBoundingClientRect();
    return !!r && clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
  };

  const isHeld = (ref: PlaneRef) =>
    isArcIn(ref.ref, arcs)
      ? arcPaths.includes(ref.ref)
      : members.some((m) => refKey(m) === refKey(ref));

  /** The holder's own file only: the item's line (or arc entry) and whatever
   *  of the plane pointed at it. The item itself is left untouched. */
  const removeHeld = (ref: PlaneRef) => {
    commitChange((t) => withoutHeld(t, path, ref));
    const key = refKey(ref);
    if (opened && (opened.kind === 'recording' ? refKey(opened.ref) === key : opened.path === ref.ref)) setOpened(null);
    if (sheet && refKey(sheet) === key) setSheet(null);
    if (writing?.path && writing.path === ref.ref) setWriting(null);
  };

  /** Clears the position only. The item stays in the nook. */
  const removeFromPlane = useCallback(
    (key: string) => commit(placements.filter((p) => refKey(p) !== key)),
    [placements, commit],
  );

  const setTint = useCallback(
    (key: string, tint: StickyTint | TagColorId) =>
      commit(
        placements.map((p) => (refKey(p) === key ? withTint(p, tint) : p)),
      ),
    [placements, commit],
  );

  const canvasAt = (clientX: number, clientY: number) =>
    cam.toCanvas(clientX, clientY, surface.current!.getBoundingClientRect());

  const connect = useLinks({
    boxes,
    links,
    refOf: (key) => cards.find((c) => c.key === key)?.placement ?? null,
    canvasAt,
    commit: commitLinks,
    onPress: () => setMenu(null),
  });

  const onCardDown = (key: string, placement: PlanePlacement, isNote: boolean) => (event: ReactPointerEvent) => {
    // A pan starting over a card is still a pan: let it reach the surface.
    if (event.button !== 0 || cam.wantsPan(event) || !surface.current) return;
    event.stopPropagation();
    setMenu(null);
    event.currentTarget.setPointerCapture(event.pointerId);
    const at = canvasAt(event.clientX, event.clientY);
    press.current = {
      key,
      isNote,
      grab: { dx: at.x - placement.x, dy: at.y - placement.y },
      start: { x: event.clientX, y: event.clientY },
      moved: false,
    };
  };

  /** A sticky's corner, pressed. Its first size is the one it is drawn at. */
  const onResizeDown = (key: string) => (event: ReactPointerEvent<HTMLElement>) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    setMenu(null);
    event.currentTarget.setPointerCapture(event.pointerId);
    const card = event.currentTarget.closest<HTMLElement>('[data-card]');
    if (!card) return;
    pull.current = {
      key,
      start: { x: event.clientX, y: event.clientY },
      from: { w: card.offsetWidth, h: card.offsetHeight },
    };
  };

  const onMove = (event: ReactPointerEvent) => {
    if (connect.move(event)) return;
    const r = pull.current;
    if (r) {
      const zoom = cam.camera.zoom;
      const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
      setSized({
        key: r.key,
        w: clamp(r.from.w + (event.clientX - r.start.x) / zoom, STICKY_SIZE.min.w, STICKY_SIZE.max.w),
        h: clamp(r.from.h + (event.clientY - r.start.y) / zoom, STICKY_SIZE.min.h, STICKY_SIZE.max.h),
      });
      return;
    }
    const p = press.current;
    if (!p || !surface.current) return;
    if (!p.moved && Math.hypot(event.clientX - p.start.x, event.clientY - p.start.y) < SLOP) return;
    p.moved = true;
    const at = canvasAt(event.clientX, event.clientY);
    setDrag({ key: p.key, x: at.x - p.grab.dx, y: at.y - p.grab.dy });
    setWriteHot(p.isNote && overWrite(event.clientX, event.clientY));
  };

  const onUp = (event: ReactPointerEvent) => {
    if (connect.up(event)) return;
    if (pull.current) {
      pull.current = null;
      if (sized) {
        commit(placements.map((pl) => (refKey(pl) === sized.key ? { ...pl, w: sized.w, h: sized.h } : pl)));
      }
      setSized(null);
      return;
    }
    const p = press.current;
    press.current = null;
    setWriteHot(false);
    if (!p) return;
    if (p.moved) justDragged.current = true;
    // A note let go over Write becomes the default and stays where it was.
    if (p.moved && p.isNote && overWrite(event.clientX, event.clientY)) {
      makeDefault(p.key);
    } else if (p.moved && drag) {
      const to = table.keepInView({ x: drag.x, y: drag.y });
      commit(placements.map((pl) => (refKey(pl) === p.key ? { ...pl, ...to } : pl)));
    }
    setDrag(null);
  };

  /** A card opened in place. A sticky is written in where it sits. */
  const open = (ref: PlaneRef, view: CardView) => {
    if (justDragged.current) {
      justDragged.current = false;
      return;
    }
    if (view.kind === 'note') {
      setSheet(null);
      setPanel(null);
      setWriting(null);
      setOpened(null);
      setNoting({ path: ref.ref, at: null });
    } else if (view.kind === 'recording') openRecording({ ref: ref.ref, anchors: ref.anchors });
    else if (view.kind === 'arc') openArc(ref.ref);
    else if (view.kind === 'nook') openNook(ref.ref);
  };

  const onDragOver = (event: DragEvent) => {
    if (!event.dataTransfer.types.includes(REF_MIME)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  /** A row dropped on the plane is placed where it landed — the one way
   *  anything gets onto the plane. Dropping one already placed moves it. */
  const onDrop = (event: DragEvent) => {
    const data = event.dataTransfer.getData(REF_MIME);
    if (!data || !surface.current) return;
    event.preventDefault();
    const ref = JSON.parse(data) as PlaneRef;
    if (!canPlace(path, ref)) return;
    const at = canvasAt(event.clientX, event.clientY);
    placeAt(ref, table.keepInView({ x: at.x - widthOfRef(ref) / 2, y: at.y - 24 }));
  };

  const widthOfRef = (ref: PlaneRef) => {
    const asNook = isNookIn(ref.ref, nooks.status === 'ready' ? nooks.workspaces : null);
    return WIDTH[cardView(ref, arcs, files, undefined, catalog)?.kind ?? (asNook ? 'nook' : 'other')];
  };

  /** Holds [ref] and places its card with its top-left at [to]: the one way a row gets onto the
   *  plane, dropped or pressed. */
  const placeAt = (ref: PlaneRef, to: Point) => {
    const asNook = isNookIn(ref.ref, nooks.status === 'ready' ? nooks.workspaces : null);
    commitChange((t) => withDropped(t, path, ref, to, asNook ? null : itemOfRef(ref)));
  };

  /** What a row dropped on the canvas is, to be held: an arc, or a recording or note. */
  const itemOfRef = (ref: PlaneRef): Item => {
    const arc = arcs?.find((a) => a.path === ref.ref);
    const kind = isArcIn(ref.ref, arcs) ? 'arc' : itemFor(ref.ref).kind === 'recording' ? 'recording' : 'note';
    return {
      path: ref.ref,
      anchor: ref.anchors[0] ?? null,
      kind,
      label: arc?.title ?? catalog.get(ref.ref)?.name ?? bare(ref.ref),
    };
  };

  // Esc closes the top-most thing. Never while something is being typed into.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const el = event.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;
      // An open picker closes itself.
      if (event.key !== 'Escape') return;
      if (tagOpen) setTagOpen(false);
      else if (connect.selected) connect.setSelected(null);
      else if (noting) setNoting(null);
      else if (sheet) setSheet(null);
      else if (opened) setOpened(null);
      else if (menu) setMenu(null);
      else if (panel) setPanel(null);
      else if (writing) setWriting(null);
      else if (table.full) table.setFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sheet, opened, menu, panel, writing, connect.selected, connect.setSelected, tagOpen, noting, table]);

  const allRows = useMemo(
    () =>
      panel && wholeVault ? vaultRows : panel ? panelRows(panel, arcPaths, members, placements, arcs, files) : [],
    [panel, wholeVault, vaultRows, arcPaths, members, placements, arcs, files],
  );
  const rows = useMemo(() => {
    const text = query.trim().toLowerCase();
    return wholeVault || !text ? allRows : allRows.filter((r) => r.title.toLowerCase().includes(text));
  }, [allRows, wholeVault, query]);
  const placedKeys = useMemo(() => new Set(placements.map(refKey)), [placements]);
  const atOf = (row: Row): Date | null => {
    const arc = arcs?.find((a) => a.path === row.ref.ref);
    if (arc) {
      const at = recencyOf(arc);
      return at > 0 ? new Date(at) : null;
    }
    return row.nook ? null : itemFor(row.ref.ref).at;
  };
  const trayEntries = useMemo(
    (): TrayEntry[] => rows.map((row) => ({ row, at: atOf(row), placed: placedKeys.has(refKey(row.ref)) })),
    // atOf reads the plan this is keyed on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, placedKeys, arcs],
  );

  useEffect(() => setQuery(''), [panel, showAll]);

  const grid = 28 * cam.camera.zoom;

  /** Holding nothing and with nothing open: three ways to start, never a
   *  seeded layout of what the nook holds. */
  const justMade =
    !scratch &&
    startedNew &&
    isJustMade(placements, arcPaths, members) &&
    panel === null &&
    writing === null &&
    opened === null &&
    sheet === null &&
    noting === null;

  const ownNook = useMemo(() => parseWorkspace(path, raw), [path, raw]);
  const waiting = ownNook ? waitingOnYou(ownNook, arcsByName) : 0;

  useCloseOnGround(opened !== null || writing !== null || noting !== null, () => {
    setOpened(null);
    setWriting(null);
    setNoting(null);
  });

  const win = pageSize();
  const notingKey = noting?.path ? refKey({ ref: noting.path, anchors: [] }) : null;
  const notingPlacement = notingKey ? placements.find((p) => refKey(p) === notingKey) : undefined;
  useEffect(() => {
    if (!noting?.tint || !notingKey || !notingPlacement) return;
    setTint(notingKey, noting.tint);
    setNoting((n) => n && { ...n, tint: undefined });
  }, [noting, notingKey, notingPlacement, setTint]);
  const openedKey = opened ? (opened.kind === 'recording' ? refKey(opened.ref) : opened.path) : null;
  /** What opened from a card hangs off it; from a list row or the nook's own arch, from the bar. */
  const trayCard = ({ row, placed }: TrayEntry) => {
    if (row.nook) {
      return (
        <NookCard
          cardKey={refKey(row.ref)}
          view={{ kind: 'nook', title: row.title, plane: row.nook.plane, nook: row.nook }}
          left={0}
          top={0}
          size={null}
          fluid
          dragging={false}
          onCanvas={placed}
          onPointerDown={() => {}}
          onOpen={() => openNook(row.ref.ref)}
          onRemove={() => {}}
          onMenu={() => {}}
        />
      );
    }
    if (trayKind === 'arcs') {
      const view = cardView(row.ref, arcs, files, undefined, catalog);
      return view ? (
        <NookCard cardKey={refKey(row.ref)} view={view} left={0} top={0} size={null} fluid dragging={false} onCanvas={placed} onPointerDown={() => {}} onOpen={() => openArc(row.ref.ref)} onRemove={() => {}} onMenu={() => {}} />
      ) : null;
    }
    return (
      <NookItemCard
        path={row.ref.ref}
        anchor={row.ref.anchors[0]}
        kind={trayKind === 'notes' ? 'note' : 'recording'}
        text={files.get(row.ref.ref) ?? null}
        nookTags={nookTags}
        placed={placed}
        onOpen={() => (trayKind === 'notes' ? openNote(row.ref.ref) : openRecording(row.ref))}
      />
    );
  };

  const detailsPlace = (() => {
    const rect = trayOpen ? trayEl?.getBoundingClientRect() : undefined;
    const main = document.querySelector('.nook-main')?.getBoundingClientRect();
    if (!rect || !main) return hangUnder({ left: panelAnchor ?? 40 }, win);
    return hangLeftOf(rect.left - main.left, win);
  })();
  const detail = (label: string, node: ReactNode) =>
    openedKey !== null && cards.some((c) => c.key === openedKey) ? (
      <CardHang table={table} surface={surface} cardKey={openedKey} kind="details" label={label}>
        {node}
      </CardHang>
    ) : (
      <BarHang kind="details" label={label} place={detailsPlace}>
        {node}
      </BarHang>
    );

  return (
    <div className={`nook${sheet ? ' nook-sheet-open' : ''}`}>
      <div className="nook-main">
        <PageBar
          words={[]}
          note={{
            buttonRef: writeButton,
            pressed: writing !== null || noting?.fromBar === true,
            hot: writeHot,
            onPress: () => {
              if (writing || noting?.fromBar) {
                setWriting(null);
                setNoting(null);
              } else openNote(lastNote.current ?? defaultPath);
            },
            onDragOver: (e) => {
              if (!e.dataTransfer.types.includes(NOTE_MIME)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'link';
              setWriteHot(true);
            },
            onDragLeave: () => setWriteHot(false),
            onDrop: (e) => {
              const note = e.dataTransfer.getData(NOTE_MIME);
              setWriteHot(false);
              if (!note) return;
              e.preventDefault();
              makeDefault(note);
            },
          }}
        />

        <div className={`table-scroll${table.full ? ' is-full' : ''}`}>
          <header className="nook-head table-lead" inert={table.full}>
            <div className="nook-head-row">
              <div className="nook-head-main">
                {!justMade && (
                  <button
                    type="button"
                    className="nook-arch"
                    aria-label="About this nook"
                    aria-pressed={ownOpen}
                    onClick={(e) => {
                      setPanelAnchor(e.currentTarget.getBoundingClientRect().left);
                      if (ownOpen) setOpened(null);
                      else openNook(path);
                    }}
                  >
                    <NookGlyph size={22} ink={isDraft ? 'var(--assistant)' : markInk(theme, nookHue)} />
                  </button>
                )}
                {!justMade && (
                  <h1 className="nook-name">
                    <EditableName
                      name={name}
                      unnamed={unnamed}
                      clearable
                      placeholder={scratch ? 'Name it to keep it' : 'Unnamed'}
                      onRename={rename}
                      renameSignal={renaming}
                    />
                  </h1>
                )}
                {!justMade && isDraft && <span className="nook-draft-word">draft</span>}
                {!justMade && waiting > 0 && <WaitingMark />}
                {!justMade && scratch && (
                  <button type="button" className="nook-btn" onClick={() => setClearing(true)}>
                    Clear
                  </button>
                )}
                {!justMade && (
                  <NookTagPicker
                    tag={nookTag ?? null}
                    hue={nookHue}
                    folders={registries.folders.all}
                    theme={theme}
                    open={tagOpen}
                    onOpen={() => setTagOpen(true)}
                    onClose={() => setTagOpen(false)}
                    onChoose={chooseTag}
                  />
                )}
              </div>
              <span className="nook-head-spacer" />
              {!justMade && isDraft && (
                <button type="button" className="nook-keep-word" onClick={keep}>
                  Keep
                </button>
              )}
              {!justMade && (
                <PinMenu
                  horizontal
                  pinned={pinned}
                  archived={archived}
                  onToggle={archived ? undefined : togglePin}
                  onArchive={archived ? restore : putAway}
                  onRename={isDraft ? () => setRenaming((n) => n + 1) : undefined}
                  onLetGo={isDraft ? putAway : undefined}
                />
              )}
            </div>
            {!justMade && startWith && !ownOpen && (
              <p className="nook-next" aria-label="What's next">
                <span className="nook-next-lead">Next</span>
                {'\u2002'}
                {startWith}
              </p>
            )}
          </header>
          <div className={`desk-top${trayOpen && !trayLifted ? ' has-tray' : ''}`}>
          <div className="desk-table">
          <Table
            table={table}
            tools={<TrayAdd open={trayOpen} onPress={() => setTrayOpen((on) => !on)} />}
            surfaceRef={surface}
            onDragOver={onDragOver}
            onDrop={onDrop}
            className={`nook-plane${drag ? ' dragging' : ''}`}
            style={
              {
                backgroundSize: `${grid}px ${grid}px`,
                backgroundPosition: `${cam.camera.x}px ${cam.camera.y}px`,
                '--dot': canvas.dot,
              } as CSSProperties
            }
            overlay={
              justMade ? (
                <JustMade
                  placeholder="A new nook"
                  name={unnamed ? '' : name}
                  onRename={rename}
                  onWrite={() => openNote(lastNote.current ?? defaultPath)}
                  onBringArcs={() => setPanel('arcs')}
                  onBringRecordings={() => setPanel('recordings')}
                />
              ) : undefined
            }
            onPointerDown={() => {
              setMenu(null);
              connect.setSelected(null);
            }}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onDoubleClickEmpty={(event) => {
              const at = canvasAt(event.clientX, event.clientY);
              setSheet(null);
              setPanel(null);
              setWriting(null);
              setOpened(null);
              setNoting({ at: { x: at.x - WIDTH.note / 2, y: at.y - 24 }, path: null });
            }}
          >
            {connect.layer}
            {cards.map(({ key, placement, view }) => {
              const at = drag?.key === key ? drag : placement;
              const size =
                sized?.key === key
                  ? sized
                  : placement.w !== undefined && placement.h !== undefined
                    ? { w: placement.w, h: placement.h }
                    : null;
              return (
                <NookCard
                  key={key}
                  cardKey={key}
                  view={view}
                  left={at.x}
                  top={at.y}
                  size={view.kind === 'note' ? size : null}
                  fill={view.kind === 'nook' ? undefined : fillOf(placement, view.kind)}
                  menuOpen={menu?.key === key}
                  dragging={drag?.key === key}
                  onPointerDown={onCardDown(key, placement, view.kind === 'note')}
                  onResizeDown={view.kind === 'note' ? onResizeDown(key) : undefined}
                  onHandleDown={connect.onHandleDown(key)}
                  linkTarget={connect.target === key}
                  selected={openedKey === key || notingKey === key}
                  open={openKeys.has(key)}
                  onOpen={() => open(placement, view)}
                  onRemove={() => removeFromPlane(key)}
                  onMenu={(x, y) => setMenu({ x, y, key })}
                  asCard={
                    view.kind === 'recording'
                      ? (shell, openProps) => (
                          <NookItemCard
                            path={placement.ref}
                            anchor={placement.anchors[0]}
                            kind="recording"
                            text={files.get(placement.ref) ?? null}
                            nookTags={nookTags}
                            minimized={!openKeys.has(key)}
                            onExpand={() => setOpenOut(key, true)}
                            onMore={(at) => setMenu({ ...at, key })}
                            shell={shell}
                            openProps={openProps}
                            onOpen={() => open(placement, view)}
                          />
                        )
                      : undefined
                  }
                />
              );
            })}
          </Table>
          </div>
          {trayOpen && (
            <Tray
              kinds={['nooks', 'recordings', 'notes', 'arcs']}
              kind={trayKind}
              onKind={(k) => setTrayKind(k as PanelKind)}
              lifted={trayLifted}
              onToggleLift={toggleLift}
              onClose={() => setTrayOpen(false)}
              scope={trayKind === 'nooks' ? undefined : { all: showAll, onChange: setShowAll }}
              query={query}
              onQuery={setQuery}
              hidden={table.full}
              maxHeight={table.height}
              trayRef={setTrayEl}
            >
              {trayKind === 'arcs' && receipt && (
                <div className="tray-receipt" role="status">
                  <span>✓ Made, at the end of this nook’s plan</span>
                  <button type="button" onClick={undoArc}>
                    Undo
                  </button>
                </div>
              )}
              {trayKind === 'arcs' && !query && (
                <button type="button" className="tray-new" onClick={() => setMaking(true)}>
                  New arc
                </button>
              )}
              <TrayList
                key={`${trayKind}${showAll}`}
                entries={trayEntries}
                query={query}
                recent={wholeVault && trayKind !== 'nooks'}
                carrying
                renderCard={trayCard}
              />
            </Tray>
          )}
          </div>
        </div>

        {panel === 'arcs' && making && (
          <BarHang kind="list" label="New arc" place={hangUnder({ left: 40 }, win, 460)} width={460}>
            <NewArcFlow
              recordings={recent}
              taken={(n) => nameTaken(n, arcs ?? [])}
              onClose={() => setMaking(false)}
              onCreate={makeArc}
            />
          </BarHang>
        )}
        {writing && (
          <BarHang
            kind="note"
            label="Writing"
            place={hangUnderRight({ right: noteRight ?? win.w - 20 }, win, barNote.size.w)}
            holdPlace
            width={barNote.size.w}
            height={Math.min(barNote.size.h, win.h - 6 - 12)}
          >
            <NookWriting
              path={writing.path}
              docked={false}
              hung
              onGripDown={barNote.onGripDown}
              placed={writing.path !== null && placements.some((p) => p.ref === writing.path && p.anchors.length === 0)}
              onPlace={() => writing.path && placeInView({ ref: writing.path, anchors: [] }, 'note')}
              onToggleDock={() => {}}
              onClose={() => setWriting(null)}
              onRemove={
                writing.path !== null && writing.path !== defaultPath && isHeld({ ref: writing.path, anchors: [] })
                  ? () => removeHeld({ ref: writing.path!, anchors: [] })
                  : undefined
              }
              mint={mint}
              onChange={noteChanged}
            />
          </BarHang>
        )}

        {opened?.kind === 'recording' &&
          files.get(opened.ref.ref) &&
          detail(
            'Recording',
            <RecordingWindow
              path={opened.ref.ref}
              raw={files.get(opened.ref.ref)!}
              anchor={opened.ref.anchors[0]}
              inArcs={arcsCiting(opened.ref.ref, arcs)}
              onOpenArc={(arc) => openArc(arc.path)}
              onExpand={() => {
                setSheet(opened.ref);
                setOpened(null);
              }}
              onRemove={isHeld(opened.ref) ? () => removeHeld(opened.ref) : undefined}
              onClose={() => setOpened(null)}
            />,
          )}
        {opened?.kind === 'arc' &&
          arcs?.find((a) => a.path === opened.path) &&
          detail(
            'Arc',
            <ArcWindow
              arc={arcs.find((a) => a.path === opened.path)!}
              onRemove={isHeld({ ref: opened.path, anchors: [] }) ? () => removeHeld({ ref: opened.path, anchors: [] }) : undefined}
              onClose={() => setOpened(null)}
            />,
          )}
        {opened?.kind === 'nook' &&
          openedNook &&
          detail(
            'Nook',
            <NookWindow
              nook={openedNook}
              name={nameOf(openedNook, catalog).named ? nameOf(openedNook, catalog).name : ''}
              arcsByName={arcsByName}
              ink={markInk(theme, openedNook.tags[0] ? tagTint(registries, openedNook.tags[0]) : null)}
              own={opened.path === path}
              startWith={opened.path === path ? startWith : undefined}
              onOpenArc={(arc) => openArc(arc.path)}
              onOpenHeld={(held, kind) => (kind === 'note' ? openNote(held.ref) : openRecording(held))}
              onClose={() => setOpened(null)}
            />,
          )}

        {noting && (notingPlacement || noting.at) && (
          <StickyPanel
            table={table}
            surface={surface}
            theme={theme}
            path={noting.path}
            cardKey={notingPlacement ? notingKey : null}
            at={noting.at}
            tone={noteTone(notingPlacement, nookHue, theme)}
            tint={notingPlacement?.tagTint ?? notingPlacement?.tint ?? noting.tint ?? null}
            onTint={(tint) => (notingKey && notingPlacement ? setTint(notingKey, tint) : setNoting((n) => n && { ...n, tint }))}
            mint={mintPlaced}
            onChange={noteChanged}
            onClose={() => setNoting(null)}
          />
        )}

        {clearing && (
          <ScratchClear
            onSave={(next) => {
              setClearing(false);
              rename(next);
            }}
            onClear={putAway}
            onCancel={() => setClearing(false)}
          />
        )}
      </div>

      {sheet && files.get(sheet.ref) && (
        <DocumentSheet
          path={sheet.ref}
          raw={files.get(sheet.ref)!}
          anchor={sheet.anchors[0]}
          inArcs={arcsCiting(sheet.ref, arcs)}
          placed={placements.some((p) => refKey(p) === refKey(sheet))}
          onPlace={() => placeInView(sheet, 'recording')}
          onOpenArc={(arc) => openArc(arc.path)}
          onClose={() => setSheet(null)}
        />
      )}

      {menu && (
        <CardMenu
          at={menu}
          isNote={cards.find((c) => c.key === menu.key)?.view.kind === 'note'}
          removeOnly={cards.find((c) => c.key === menu.key)?.view.kind === 'nook'}
          fold={
            ['recording', 'nook'].includes(cards.find((c) => c.key === menu.key)?.view.kind ?? '')
              ? { minimized: !openKeys.has(menu.key), onToggle: () => setOpenOut(menu.key, !openKeys.has(menu.key)) }
              : undefined
          }
          name={cardName(cards.find((c) => c.key === menu.key)?.view)}
          theme={theme}
          onTint={(tint) => setTint(menu.key, tint)}
          onRemove={() => removeFromPlane(menu.key)}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}

/** The plane before the file has arrived: drawn at once, with nothing on it
 *  and no spinner over it. */
export function NookLoading() {
  return (
    <div className="nook">
      <div className="plane nook-plane" style={{ backgroundSize: '28px 28px' }} />
    </div>
  );
}

function JustMade({
  placeholder,
  name,
  onRename,
  onWrite,
  onBringArcs,
  onBringRecordings,
}: {
  placeholder: string;
  name: string;
  onRename: (name: string) => void;
  onWrite: () => void;
  onBringArcs: () => void;
  onBringRecordings: () => void;
}) {
  const [draft, setDraft] = useState(name);
  useEffect(() => setDraft(name), [name]);
  /** Esc puts the name back; the blur it causes must not keep the draft. */
  const dropped = useRef(false);
  const keep = () => {
    if (dropped.current) {
      dropped.current = false;
      return;
    }
    const next = draft.replace(/\s+/g, ' ').trim();
    if (next && next !== name) onRename(next);
  };

  return (
    <div className="nook-start">
      <input
        className="nook-start-name"
        aria-label="Nook name"
        placeholder={placeholder}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={keep}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') {
            dropped.current = true;
            setDraft(name);
            e.currentTarget.blur();
          }
        }}
      />
      <div className="nook-start-pills">
        <button type="button" className="nook-pill" onClick={onWrite}>
          <PenGlyph /> Write
        </button>
        <button type="button" className="nook-pill" onClick={onBringArcs}>
          Bring arcs
        </button>
        <button type="button" className="nook-pill" onClick={onBringRecordings}>
          Bring recordings
        </button>
      </div>
    </div>
  );
}

/** A placed nook as the nooks page draws it. */
export function PlacedNook({ nook, open = false }: { nook: Workspace; open?: boolean }) {
  const { theme } = useTheme();
  const { plan } = usePlan();
  const catalog = useCatalog();
  const arcs = plan.status === 'ready' ? plan.plan.arcs : null;
  const registries = plan.status === 'ready' ? plan.plan.registries : EMPTY_REGISTRIES;
  const byName = useMemo(() => (arcs ? new Map(arcs.map((a) => [a.name, a])) : null), [arcs]);
  const look = useMiniLook(arcs, catalog, registries, theme);
  return (
    <NookBoardCard
      inert
      workspace={nook}
      arcs={byName}
      catalog={catalog}
      hue={nook.tags[0] ? tagTint(registries, nook.tags[0]) : null}
      theme={theme}
      look={look}
      open={open}
    />
  );
}

export function NookCard({
  cardKey,
  view,
  left,
  top,
  size,
  fill,
  menuOpen = false,
  dragging,
  onPointerDown,
  onResizeDown,
  onHandleDown,
  linkTarget = false,
  selected = false,
  open = false,
  onCanvas = false,
  fluid = false,
  onOpen,
  onRemove,
  onMenu,
  asCard,
  className,
}: {
  cardKey: string;
  view: CardView;
  left: number;
  top: number;
  /** A sticky's own size; without one it is its kind's width and its text's
   *  height. */
  size: { w: number; h: number } | null;
  /** What it wears, over its kind's own fill. */
  fill?: string;
  /** Its ⋮ menu is open. */
  menuOpen?: boolean;
  dragging: boolean;
  onPointerDown: (event: ReactPointerEvent) => void;
  /** Offered on a sticky: its corner pulls it to a new size. */
  onResizeDown?: (event: ReactPointerEvent<HTMLElement>) => void;
  /** One of the four side handles, pressed: a connection starts there. A canvas with no
   *  connections leaves it out and no handles are drawn. */
  onHandleDown?: (side: Side) => (event: ReactPointerEvent<HTMLElement>) => void;
  /** A connection being drawn would land here. */
  linkTarget?: boolean;
  /** Its details are open. */
  selected?: boolean;
  /** Drawn opened out, where its kind has an opened state: a nook's. */
  open?: boolean;
  /** Already on the canvas the page draws: the app's mark in its head. */
  onCanvas?: boolean;
  /** As wide as what holds it, as in the tray, rather than its kind's width. */
  fluid?: boolean;
  /** Words the page adds to the card's own. */
  className?: string;
  onOpen: () => void;
  onRemove: () => void;
  onMenu: (x: number, y: number) => void;
  /** Drawn as the card it was dragged from: this card holds its surface and
   *  hands its open target the press, the keys and the menu, so it still
   *  moves, links, opens and goes like any other. */
  asCard?: (
    shell: (card: CardShell) => ReactNode,
    openProps: ButtonHTMLAttributes<HTMLButtonElement>,
  ) => ReactNode;
}) {
  const width = view.kind === 'nook' && open ? NOOK_WIDTH.open : WIDTH[view.kind];
  const style: CSSProperties = { left, top, width: fluid ? '100%' : (size?.w ?? width) };
  if (size) style.height = size.h;
  if (view.kind === 'note') {
    style.background = `var(${view.tint === 'green' ? '--stickyGreen' : '--stickyWarm'})`;
  }
  if (fill) style.background = fill;
  const state = `${dragging ? ' dragging' : ''}${linkTarget ? ' link-target' : ''}${selected ? ' is-selected' : ''}`;

  const acts = {
    'data-card': true,
    'data-card-key': cardKey,
    onPointerDown,
    onClick: onOpen,
    onKeyDown: (e: ReactKeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        onRemove();
      }
    },
    onContextMenu: (e: ReactMouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      onMenu(e.clientX, e.clientY);
    },
  };
  const handles = (onHandleDown ? SIDES : []).map((side) => (
    <span
      key={side}
      className={`nook-handle nook-handle-${side}`}
      aria-hidden="true"
      onPointerDown={onHandleDown!(side)}
      // The drag ends over the card; it must not also open it.
      onClick={(e) => e.stopPropagation()}
    />
  ));

  if (asCard) {
    const { onPointerDown: press, onKeyDown, onContextMenu } = acts;
    return (
      <>
        {asCard(
          (card) => (
            <div
              data-card
              data-card-key={cardKey}
              className={`nook-card ${card.className}${state}${className ? ` ${className}` : ''}`}
              style={{ ...card.style, left, top, width }}
            >
              {card.content}
              {handles}
            </div>
          ),
          { onPointerDown: press, onKeyDown, onContextMenu },
        )}
      </>
    );
  }

  return (
    <button
      type="button"
      {...acts}
      className={`nook-card nook-card-${view.kind}${menuOpen ? ' menu-open' : ''}${size ? ' sized' : ''}${state}${className ? ` ${className}` : ''}`}
      style={style}
    >
      {view.kind === 'arc' && <ArcLine title={view.title} status={view.status} inside={view.inside} onCanvas={onCanvas} />}
      {view.kind === 'note' && (
        <span className="nook-sticky-body">
          {view.title && <span className="nook-sticky-title">{view.title}</span>}
          {view.text && <span className="nook-sticky-text">{view.text}</span>}
        </span>
      )}
      {handles}
      {onResizeDown && (
        <span
          className="nook-resize"
          aria-hidden="true"
          onPointerDown={onResizeDown}
          // The pull ends over the card; it must not also open it.
          onClick={(e) => e.stopPropagation()}
        />
      )}
      {view.kind === 'nook' && (
        <>
          <PlacedNook nook={view.nook} open={open} />
          {onCanvas && (
            <span className="nook-card-canvas">
              <CanvasMark />
            </span>
          )}
          <span
            className="nook-card-more"
            role="button"
            tabIndex={0}
            aria-label="More"
            aria-expanded={menuOpen}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              const at = e.currentTarget.getBoundingClientRect();
              onMenu(at.left, at.bottom + 4);
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            ⋮
          </span>
        </>
      )}
      {view.kind === 'other' && <span className="nook-face">{view.title}</span>}
    </button>
  );
}

/**
 * A list hung off a bar word: one row to a line, over what the page holds. Rows are carried onto
 * the table to place them and, where the order is the page's own, up and down to reorder.
 */
export function ListPanel({
  kind,
  rows,
  onReorder,
  done,
  showAll,
  search,
  onPickStatus,
  onNewArc,
  onAdd,
  fresh = null,
  receipt = null,
  onRemove,
  waiting,
  onOpen,
  onPlace,
  isPlaced,
  tintOf,
  carry = true,
}: {
  kind: PanelKind;
  rows: Row[];
  /** The panel's one way to start an arc, below its last one. */
  onNewArc?: () => void;
  /** Puts something that already exists into the nook. */
  onAdd?: () => void;
  /** An arc just made, washed for a moment. */
  fresh?: string | null;
  /** Said at the foot while a new arc's writes can still be taken back. */
  receipt?: { onUndo: () => void } | null;
  /** Offered where the list's order is the nook's own — its arcs. */
  onReorder?: (order: string[]) => void;
  /** The arcs panel's switch for its done arcs, and how many there are. */
  done?: { count: number; shown: boolean; onToggle: () => void };
  /** The switch for listing the whole vault rather than what the nook holds. */
  showAll?: { on: boolean; onToggle: () => void };
  /** Narrows a whole-vault list; shown only while it is one. */
  search?: { value: string; onChange: (value: string) => void };
  onPickStatus?: (row: Row, anchor: DOMRect) => void;
  /** Offered on a row's ⋮, for the rows it returns true for. */
  onRemove?: { run: (row: Row) => void; offered: (row: Row) => boolean };
  /** How many arcs wait on you in a nook's row. */
  waiting?: (row: Row) => number;
  onOpen: (row: Row) => void;
  /** Puts a row that is not on the table into its first free gap, where dragging is not to hand. */
  onPlace?: (row: Row) => void;
  /** Whether a row is already on the table; it is marked, and offers no place. */
  isPlaced?: (row: Row) => boolean;
  /** What a row is filled with: a note's row wears the colour its sticky does. */
  tintOf?: (row: Row) => string | undefined;
  /** Whether a row can be picked up and carried onto the table. */
  carry?: boolean;
}) {
  const { theme } = useTheme();
  const { plan } = usePlan();
  const registries = plan.status === 'ready' ? plan.plan.registries : EMPTY_REGISTRIES;
  /** The row being carried, when it was picked up here. A drag's data cannot
   *  be read until the drop, so the panel remembers what it handed out. */
  const [carrying, setCarrying] = useState<string | null>(null);
  /** The carried row has left the list and its neighbours closed over it. */
  const [lifted, setLifted] = useState(false);
  /** The carried row's height: the room the others make where it would land. */
  const [room, setRoom] = useState(0);
  /** Where it would land — before this row, or at the end for null — and the
   *  top of the room opened for it. */
  const [landing, setLanding] = useState<Landing | null>(null);
  /** One frame without transitions, for a change that must not animate: the
   *  row leaving on lift, or landing on drop. */
  const [settling, setSettling] = useState(false);
  const liftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const [rowMenu, setRowMenu] = useState<{ x: number; y: number; row: Row } | null>(null);

  const more = (row: Row) =>
    onRemove?.offered(row) ? (
      <button
        type="button"
        className="nook-row-more"
        aria-label="More"
        aria-expanded={rowMenu?.row === row}
        draggable={false}
        onClick={(e) => {
          e.stopPropagation();
          const at = e.currentTarget.getBoundingClientRect();
          setRowMenu({ x: at.left, y: at.bottom + 4, row });
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="1.7" />
          <circle cx="12" cy="12" r="1.7" />
          <circle cx="19" cy="12" r="1.7" />
        </svg>
      </button>
    ) : null;

  useLayoutEffect(() => {
    if (!settling) return;
    // The browser has to see the new positions with transitions off before
    // they come back on, or it animates the jump anyway.
    void list.current?.offsetHeight;
    const id = requestAnimationFrame(() => setSettling(false));
    return () => cancelAnimationFrame(id);
  }, [settling]);

  useEffect(() => () => {
    if (liftTimer.current) clearTimeout(liftTimer.current);
  }, []);

  const order = rows.map((row) => row.ref.ref);

  /** Measured on layout, not on screen: rows moved aside to make room are
   *  still where the list lays them out, so the answer does not chase them. */
  const landingAt = (clientY: number): Landing => {
    const box = list.current;
    if (!box) return { before: null, top: 0 };
    const y = clientY - box.getBoundingClientRect().top + box.scrollTop;
    let end = parseFloat(getComputedStyle(box).paddingTop) || 0;
    for (const el of box.querySelectorAll<HTMLElement>('[data-row-ref]')) {
      if (el.dataset['rowRef'] === carrying) continue;
      if (y < el.offsetTop + el.offsetHeight / 2) return { before: el.dataset['rowRef'] ?? null, top: el.offsetTop };
      end = el.offsetTop + el.offsetHeight;
    }
    return { before: null, top: end };
  };

  const settle = () => {
    if (liftTimer.current) clearTimeout(liftTimer.current);
    liftTimer.current = null;
    setCarrying(null);
    setLifted(false);
    setLanding(null);
  };

  const pickUp = (event: DragEvent<HTMLDivElement>, row: Row) => {
    const el = event.currentTarget;
    event.dataTransfer.setData(REF_MIME, JSON.stringify({ ref: row.ref.ref, anchors: row.ref.anchors }));
    if (kind === 'notes') event.dataTransfer.setData(NOTE_MIME, row.ref.ref);
    event.dataTransfer.effectAllowed = 'copyMove';
    liftImage(event, el);
    if (!onReorder) return;
    const at = order.indexOf(row.ref.ref);
    const ownSlot: Landing = { before: order[at + 1] ?? null, top: el.offsetTop };
    setCarrying(row.ref.ref);
    setRoom(el.offsetHeight);
    // Only once the browser has its image: hiding the source inside dragstart
    // cancels the drag. It leaves with its room held open, so nothing moves.
    liftTimer.current = setTimeout(() => {
      liftTimer.current = null;
      setLifted(true);
      setLanding(ownSlot);
      setSettling(true);
    });
  };

  const shiftFrom = landing?.before != null ? order.indexOf(landing.before) : -1;
  const hiddenDone = done !== undefined && done.count > 0 && !done.shown;
  const tools = (showAll || (done && done.count > 0)) && (
    <div className="hang-tools">
      {showAll && (
        <button
          type="button"
          className="nook-done-toggle"
          aria-pressed={showAll.on}
          title={showAll.on ? 'List only what this nook holds' : 'List the whole vault'}
          onClick={showAll.onToggle}
        >
          Show all
        </button>
      )}
      {done && done.count > 0 && (
        <button
          type="button"
          className="nook-done-toggle"
          aria-pressed={done.shown}
          title={done.shown ? 'Hide done arcs' : 'Show done arcs'}
          onClick={done.onToggle}
        >
          Done {done.count}
        </button>
      )}
    </div>
  );

  return (
    <>
      {tools}
      {search && (
        <input
          className="nook-panel-search"
          type="search"
          aria-label={`Search ${kind}`}
          placeholder={kind === 'nooks' ? 'Search nooks' : 'Search the vault'}
          value={search.value}
          onChange={(e) => search.onChange(e.target.value)}
        />
      )}
      <div
        ref={list}
        className={`hang-rows${settling ? ' settling' : ''}`}
        // Let go in the list and it reorders; let go on the plane and it is
        // placed. One gesture, and where it ends says which.
        onDragOver={(e) => {
          if (!onReorder || carrying === null) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          if (!lifted) return;
          const at = landingAt(e.clientY);
          setLanding((l) => (l && l.before === at.before && l.top === at.top ? l : at));
        }}
        onDragLeave={(e) => {
          if (!list.current?.contains(e.relatedTarget as Node | null)) setLanding(null);
        }}
        onDrop={(e) => {
          if (!onReorder || carrying === null) return;
          e.preventDefault();
          const next = movedBefore(order, carrying, landingAt(e.clientY).before);
          if (next.some((p, i) => p !== order[i])) onReorder(next);
          setSettling(true);
          settle();
        }}
      >
        {rows.length === 0 && (
          <p className="nook-panel-empty">
            {search?.value.trim() ? 'Nothing matches.' : hiddenDone ? 'Every arc here is done.' : 'Nothing here yet.'}
          </p>
        )}
        {rows.map((row, i) => {
          const isCarried = carrying === row.ref.ref;
          const shifted = landing !== null && !isCarried && shiftFrom >= 0 && i >= shiftFrom;
          const carried =
            `${isCarried ? ' carrying' : ''}${isCarried && lifted ? ' lifted' : ''}` +
            `${row.ref.ref === fresh ? ' fresh' : ''}`;
          return (
            <div
              key={refKey(row.ref)}
              role="button"
              tabIndex={0}
              className={`nook-row nook-row-${kind}${carried}`}
              style={
                {
                  ...(shifted ? { transform: `translateY(${room}px)` } : null),
                  ...(kind === 'notes' && tintOf?.(row) ? { '--row-tint': tintOf(row) } : null),
                } as CSSProperties
              }
              data-row-ref={row.ref.ref}
              draggable={carry}
              onDragStart={(e) => pickUp(e, row)}
              onDragEnd={settle}
              onClick={() => onOpen(row)}
              onKeyDown={(e) => {
                if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return;
                e.preventDefault();
                onOpen(row);
              }}
            >
              {kind === 'arcs' ? (
                <ArcLine
                  title={row.title}
                  status={row.status ?? 'next'}
                  inside={row.inside}
                  onPickStatus={onPickStatus && ((anchor) => onPickStatus(row, anchor))}
                />
              ) : kind === 'nooks' && row.nook ? (
                <span className="nook-row-text nook-row-nook">
                  {row.title ? (
                    <>
                      <NookGlyph size={14} ink={markInk(theme, row.nook.tags[0] ? tagTint(registries, row.nook.tags[0]) : null)} />
                      <span className="nook-row-title">{row.title}</span>
                      {(waiting?.(row) ?? 0) > 0 && <WaitingMark />}
                    </>
                  ) : (
                    <NookThumb nook={row.nook} />
                  )}
                </span>
              ) : (
                <span className="nook-row-text">
                  <span className="nook-row-title">{row.title}</span>
                  {kind === 'recordings' && <RowDate row={row} />}
                </span>
              )}
              {isPlaced?.(row) ? (
                <span className="nook-row-placed">
                  <CanvasMark />
                </span>
              ) : (
                onPlace && (
                  <button
                    type="button"
                    className="nook-row-place"
                    aria-label={`Place ${row.title || 'it'} on the table`}
                    title="Place on the table"
                    draggable={false}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPlace(row);
                    }}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <PlaceGlyph />
                  </button>
                )
              )}
              {more(row)}
            </div>
          );
        })}
        {onNewArc && !lifted && (
          <button type="button" className="nook-row-new" onClick={onNewArc}>
            <span aria-hidden="true">＋</span> New arc
          </button>
        )}
        {onAdd && !lifted && (
          <button type="button" className="nook-row-new" onClick={onAdd}>
            <span aria-hidden="true">＋</span> Add…
          </button>
        )}
        {landing && (
          <span className="nook-row-slot" style={{ top: landing.top, height: room }} aria-hidden="true" />
        )}
        {onReorder && <span className="nook-rows-end" style={{ height: landing ? room : 0 }} aria-hidden="true" />}
      </div>
      {receipt && (
        <div className="nook-receipt" role="status">
          <span>✓ Made, at the end of this nook's plan</span>
          <button type="button" onClick={receipt.onUndo}>
            Undo
          </button>
        </div>
      )}
      {rowMenu && onRemove && (
        <HeldMenu
          at={rowMenu}
          onRemove={() => onRemove.run(rowMenu.row)}
          onClose={() => setRowMenu(null)}
        />
      )}
    </>
  );
}

function RowDate({ row }: { row: Row }) {
  const date = row.date ?? capturedLabel(row.ref.ref, '');
  return date ? <span className="nook-date">{date}</span> : null;
}

function PlaceGlyph() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

/** What a hovered item in a nook's panel offers: taking it out of this nook. */
export function HeldMenu({
  at,
  onRemove,
  onClose,
}: {
  at: { x: number; y: number };
  onRemove: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest('.plane-menu')) onClose();
    };
    const id = setTimeout(() => document.addEventListener('pointerdown', away));
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', away);
    };
  }, [onClose]);

  return (
    <div className="plane-menu" style={{ left: at.x, top: at.y }} role="menu">
      <button
        type="button"
        role="menuitem"
        onClick={() => {
          onRemove();
          onClose();
        }}
      >
        Remove from here
      </button>
    </div>
  );
}

interface Landing {
  before: string | null;
  top: number;
}

/** Room around the drag image, so its shadow is part of the picture. */
const GHOST_PAD = 16;

/** The drag image: the row lifted off the list as a card, instead of the
 *  browser's faint copy of it. */
function liftImage(event: DragEvent<HTMLElement>, row: HTMLElement) {
  const rect = row.getBoundingClientRect();
  const ghost = document.createElement('div');
  ghost.className = 'nook-row-ghost';
  const copy = row.cloneNode(true) as HTMLElement;
  copy.removeAttribute('data-row-ref');
  copy.style.width = `${rect.width}px`;
  ghost.appendChild(copy);
  (row.closest('.hang') ?? document.body).appendChild(ghost);
  event.dataTransfer.setDragImage(ghost, event.clientX - rect.left + GHOST_PAD, event.clientY - rect.top + GHOST_PAD);
  setTimeout(() => ghost.remove());
}

export function CardMenu({
  at,
  isNote,
  removeOnly = false,
  fold,
  name,
  theme = 'light',
  onTint,
  onRemove,
  onClose,
}: {
  at: { x: number; y: number };
  isNote: boolean;
  /** A placed nook offers nothing but taking it off the canvas. */
  removeOnly?: boolean;
  /** What the card shows as its name; copied by "Copy name". */
  name?: string;
  /** A recording's card: one row, or the whole card. */
  fold?: { minimized: boolean; onToggle: () => void };
  theme?: Theme;
  onTint: (tint: StickyTint | TagColorId) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const away = (e: PointerEvent) => {
      if (!(e.target as HTMLElement).closest('.plane-menu')) onClose();
    };
    const id = setTimeout(() => document.addEventListener('pointerdown', away));
    return () => {
      clearTimeout(id);
      document.removeEventListener('pointerdown', away);
    };
  }, [onClose]);

  const run = (fn: () => void) => () => {
    fn();
    onClose();
  };

  return (
    <div className="plane-menu" style={{ left: at.x, top: at.y }} role="menu">
      {fold && (
        <>
          <button type="button" role="menuitem" onClick={run(fold.onToggle)}>
            {fold.minimized ? 'Expand' : 'Minimize'}
          </button>
          <span className="plane-menu-sep" />
        </>
      )}
      {isNote && (
        <>
          <button type="button" role="menuitem" onClick={run(() => onTint('warm'))}>
            <span className="nook-swatch" style={{ background: 'var(--stickyWarm)' }} /> Warm
          </button>
          <button type="button" role="menuitem" onClick={run(() => onTint('green'))}>
            <span className="nook-swatch" style={{ background: 'var(--stickyGreen)' }} /> Green
          </button>
          <div className="plane-menu-swatches" role="group" aria-label="Tag colours">
            {TAG_COLOR_IDS.map((id) => (
              <button
                key={id}
                type="button"
                role="menuitem"
                className="nook-swatch plane-menu-swatch"
                aria-label={TAG_COLORS[id].name}
                title={TAG_COLORS[id].name}
                style={{ background: tagTones(theme, id).band }}
                onClick={run(() => onTint(id))}
              />
            ))}
          </div>
          <span className="plane-menu-sep" />
        </>
      )}
      {name && !removeOnly && (
        <button type="button" role="menuitem" onClick={run(() => void navigator.clipboard?.writeText(name))}>
          Copy name
        </button>
      )}
      <button type="button" role="menuitem" onClick={run(onRemove)}>
        Remove from the plane <kbd className="kbd">⌫</kbd>
      </button>
    </div>
  );
}

function bare(ref: string): string {
  return ref.replace(/^.*\//, '').replace(/\.md$/, '');
}
